import './screens.css';
import type { Level } from '../../cards/difficulty';
import type { ShopCard, ShopId } from '../../cards/shop';
import { onLevel } from '../../game/progress';
import type { Progress } from '../../game/progress';
import { t } from '../../i18n/i18n';
import { isTyping } from '../../input/typing';
import { Signal } from '../../rules/signal';
import { credits, licensesFile } from './credits';
import { el } from './dom';
import { homeScreenHint } from './phone';

/**
 * The screens between bouts, over the arena: the menu with the title, the coins, the level, FIGHT and SHOP; and the
 * shop, where each punch shows how it is thrown and its price; and, out of the way, whose work the game is built on.
 * They only show and ask; the game decides.
 */
export class Screens {
  readonly fightPressed = new Signal<void>();
  readonly shopPressed = new Signal<void>();
  readonly backPressed = new Signal<void>();
  readonly buyPressed = new Signal<ShopId>();
  readonly levelPicked = new Signal<string>();
  private readonly menu: HTMLElement;
  private readonly menuCoins: HTMLElement;
  private readonly levels: HTMLElement;
  private readonly stakes: HTMLElement;
  private readonly shop: HTMLElement;
  private readonly shopCoins: HTMLElement;
  private readonly items: HTMLElement;
  private readonly credits: HTMLElement;

  constructor(root: HTMLElement) {
    this.menu = el('div', 'screen menu', root);
    const menuCard = el('div', 'menu-card', this.menu);
    const title = el('h1', 'title', menuCard);
    el('span', 'title-main', title, t('game-title'));
    el('span', 'title-sub', title, t('game-subtitle'));
    this.menuCoins = el('div', 'coins', menuCard);
    const difficulty = el('div', 'difficulty', menuCard);
    el('div', 'difficulty-label', difficulty, t('difficulty'));
    this.levels = el('div', 'levels', difficulty);
    this.stakes = el('div', 'stakes', difficulty);
    const buttons = el('div', 'menu-buttons', menuCard);
    el('button', 'big-button', buttons, t('fight')).addEventListener('click', () => this.fightPressed.emit());
    el('button', 'glass-button', buttons, t('shop')).addEventListener('click', () => this.shopPressed.emit());
    const hint = homeScreenHint();
    if (hint) el('p', 'home-screen-hint', this.menu, hint);
    el('button', 'credits-button', this.menu, t('credits')).addEventListener('click', () => this.showCredits(true));

    this.shop = el('div', 'screen shop', root);
    const shopCard = el('div', 'shop-card', this.shop);
    const head = el('div', 'shop-head', shopCard);
    el('h2', '', head, t('shop'));
    this.shopCoins = el('div', 'coins', head);
    el('p', 'shop-hint', shopCard, t('shop-hint'));
    this.items = el('div', 'shop-items', shopCard);
    el('button', 'glass-button', shopCard, t('back')).addEventListener('click', () => this.backPressed.emit());

    this.credits = el('div', 'screen credits', root);
    const creditsCard = el('div', 'credits-card', this.credits);
    el('h2', '', creditsCard, t('credits-title'));
    for (const credit of credits) {
      const line = el('p', '', creditsCard, t(credit.text));
      for (const link of credit.links) {
        line.append(' ');
        this.link(line, t(link.label), link.href);
      }
    }
    this.link(el('p', '', creditsCard), t('credits-all-licenses'), licensesFile);
    el('button', 'glass-button', creditsCard, t('back')).addEventListener('click', () => this.showCredits(false));

    window.addEventListener('keydown', (event) => {
      if (isTyping(event)) return;
      if (event.code === 'Enter' && this.menu.classList.contains('shown')) this.fightPressed.emit();
      if (event.code === 'Escape' && this.shop.classList.contains('shown')) this.backPressed.emit();
      if (event.code === 'Escape' && this.credits.classList.contains('shown')) this.showCredits(false);
    });
  }

  showMenu(progress: Progress, levels: readonly Level[], shop: ShopCard): void {
    this.menuCoins.textContent = t('coins', progress.coins);
    this.levels.replaceChildren();
    for (const level of levels) {
      const picked = level.id === progress.level;
      const button = el('button', `level-button${picked ? ' picked' : ''}`, this.levels, t(level.nameKey));
      button.dataset.level = level.id;
      button.addEventListener('click', () => this.levelPicked.emit(level.id));
      if (picked) this.stakes.textContent = t('level-stakes', onLevel(shop.coinsForWin, level), level.coinsLost);
    }
    this.shop.classList.remove('shown');
    this.credits.classList.remove('shown');
    this.menu.classList.add('shown');
  }

  showShop(progress: Progress, card: ShopCard): void {
    this.shopCoins.textContent = t('coins', progress.coins);
    this.items.replaceChildren();
    for (const item of card.items) {
      const owned = progress.owned.has(item.id);
      const row = el('div', `shop-item${owned ? ' owned' : ''}`, this.items);
      el('div', 'shop-name', row, t(`punch-${item.id}`));
      el('div', 'shop-how', row, t(`how-${item.id}`));
      const button = el('button', 'buy-button', row, owned ? t('owned') : t('buy', item.price));
      button.disabled = owned || progress.coins < item.price;
      button.addEventListener('click', () => this.buyPressed.emit(item.id));
    }
    this.menu.classList.remove('shown');
    this.shop.classList.add('shown');
  }

  hide(): void {
    this.menu.classList.remove('shown');
    this.shop.classList.remove('shown');
    this.credits.classList.remove('shown');
  }

  private showCredits(on: boolean): void {
    this.credits.classList.toggle('shown', on);
    this.menu.classList.toggle('shown', !on);
  }

  private link(parent: HTMLElement, text: string, href: string): void {
    const link = el('a', '', parent, text);
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener';
  }
}
